import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import Header from './components/Header';
import './styles/EditStudents.css';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import ConfirmationModal from './components/ConfirmationModal';
import LoadingPage from './components/LoadingPage';
import ErrorPage from './components/ErrorPage';

const EditStudents = () => {
  const [students, setStudents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortCriteria, setSortCriteria] = useState('grade');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL;

  // Fetch all users data
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/students/all`);
        setStudents(response.data);
        setLoading(false);
      } catch (err) {
        setError(err);
        setLoading(false);
      }
    };

    fetchUsers();
  }, [API_URL]);

  // If add-student button is clicked, navigate to add-student page
  const handleAddStudent = () => {
    navigate('/add-student');
  };

  // If update-student button is clicked, navigate to update-student page
  const handleUpdate = (id) => {
    navigate(`/update-student/${id}`);
  };

  // If delete-student button is clicked, open confirmation modal
  const handleDelete = (id) => {
    setStudentToDelete(id);
    setIsModalOpen(true);
  };

  // Delete a student
  const confirmDelete = async () => {
    try {
      await axios.delete(`${API_URL}/api/students/${studentToDelete}`, {}); // Call the API to delete the student
      setStudents(students.filter(student => student._id !== studentToDelete)); // Set the students to filter out the deleted student
      toast.success('Student deleted successfully!', {
        iconTheme: {
          primary: '#333',
          secondary: '#DCB41F',
        },
        style: {
          backgroundColor: '#333',
          color: '#DCB41F',
        },
      });

      setIsModalOpen(false); // Close modal
      setStudentToDelete(null); // Reset studentToDelete
      await new Promise(resolve => setTimeout(resolve, 1000)); // Wait for 1 second before reloading the page
      window.location.reload();

    } catch (err) {
      setError(err);
      toast.error(err.message); // Display error message in toast
      setIsModalOpen(false); // Close modal
      setStudentToDelete(null); // Reset studentToDelete
    }
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  // Handle sort change
  const handleSortChange = (e) => {
    const value = e.target.value;
    setSortCriteria(value);
    sortStudents(value, students);
  };

  const sortStudents = useCallback((criteria, studentsList) => {
    let sortedStudents;
    if (criteria === 'lastname') {
      sortedStudents = [...studentsList].sort((a, b) =>
        (a.last_name || '').localeCompare(b.last_name || '')
      );
    } else if (criteria === 'firstname') {
      sortedStudents = [...studentsList].sort((a, b) =>
        (a.first_name || '').localeCompare(b.first_name || '')
      );
    } else if (criteria === 'grade') {
      sortedStudents = [...studentsList].sort((a, b) => (b.grade || 0) - (a.grade || 0));
    }
    setStudents(sortedStudents);
  }, []);

  // Sort students based on the sort criteria
  useEffect(() => {
    if (students.length > 0) {
      sortStudents(sortCriteria, students);
    }
  }, [students, sortCriteria, sortStudents]);

  const filteredStudents = students.filter(student => {
    const fullName = `${student.first_name || ''} ${student.last_name || ''}`.toLowerCase();
    const grade = `${student.grade || ''}`.toLowerCase();
    const tools = student.list_of_trained_tools || [];

    return (
      fullName.includes(searchTerm.toLowerCase()) ||
      grade.includes(searchTerm.toLowerCase()) ||
      tools.some(tool => tool.tool_name && tool.tool_name.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  });

  if (loading) return <LoadingPage />;
  if (error) return <ErrorPage />;

  return (
    <div>
      <Header />
      <div className="edit-records-container">
        <div className="tab-container">
          <button className="tab selected">Students</button>
          <button className="tab" onClick={() => navigate('/edit-tools')}>Tools</button>
        </div>
        <div className="controls-container">
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="search-input"
          />
          <select value={sortCriteria} onChange={handleSortChange} className="sort-dropdown">
            <option value="lastname">Sort By: Last Name A → Z </option>
            <option value="firstname">Sort By: First Name A → Z </option>
            <option value="grade">Sort By: Grade Level</option>
          </select>
          <button className="add-student-button" onClick={handleAddStudent}>Add a new student</button>
        </div>
        <table className="students-table">
          <thead>
            <tr>
              <th>Student ID</th>
              <th>Student Name</th>
              <th>Grade Level</th>
              <th>Admin Status</th>
              <th>List of Tools/Experience</th>
              <th>Update Student</th>
              <th>Remove Student</th>
            </tr>
          </thead>
          <tbody>
            {filteredStudents.map(student => (
              <tr key={student._id}>
                <td>{student.user_id}</td>
                <td>{`${student.first_name} ${student.last_name}`}</td>
                <td>{student.grade}</td>
                <td>{student.is_admin ? 'Admin' : 'Student'}</td>
                <td>{student.list_of_trained_tools.map(tool => tool.tool_name).join(', ')}</td>
                <td><button className="update-button" onClick={() => handleUpdate(student._id)}>Update</button></td>
                <td><button className="delete-button" onClick={() => handleDelete(student._id)}>Delete</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={confirmDelete}
        message="Are you sure you want to delete this student?"
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};

export default EditStudents;
